import {
  BoundingSphere,
  Cartesian2,
  Cartesian3,
  Cartographic,
  Color,
  createGuid,
  Math as cesiumMath,
  PolygonPipeline,
} from "@cesium/engine";
import Utils from "../basic/Utils";
import DitapViewer from "../basic/DitapViewer";

/**
 * 면적 계산 및 영역 표시 클래스.<br>
 * Area calculation and area display class.
 *
 * @alias MeasurementArea
 * @constructor
 *
 * @param {object} options 각각 다음과 같은 속성을 가진 객체. </br> Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Color} [options.entityColor = Color.fromBytes(229, 30, 110, 51)] 엔티티 색상.<br> Color of the entity.
 * @param {Cartesian3[]} [options.positions = []] 면적 거리 측정을 위한 Cartesian 좌표 배열.</br> Cartesian coordinate array for measuring area.
 *
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Area Measurement' 참조
 *
 * const viewer = new DitapViewer("ditapContainer");
 *
 * // 1. 지정된 영역 생성. (InteractionMeasurementArea 통한 수정 불가.)
 * const position1 = new Ditap.Cartesian3.fromDegrees(126.9777639655642, 37.56676148138595);
 * const position2 = new Ditap.Cartesian3.fromDegrees(126.97776396708979, 37.56658104096736);
 * const position3 = new Ditap.Cartesian3.fromDegrees(126.97794279498576, 37.566579036698215);
 * const position4 = new Ditap.Cartesian3.fromDegrees(126.9779494810781, 37.56676163390728);
 *
 * const options = {
 *  viewer: viewer,
 *  positions: [position1, position2, position3, position4],
 *  entityColor: Color.fromBytes(38, 60, 150, 255)
 * }
 *
 * const measurementArea = new Ditap.MeasurementArea(options);
 *
 *
 * // 2. 마우스로 영역 지정 후 생성. (InteractionMeasurementArea를 통해 면 엔티티 표시.)
 * const measurementArea = new Ditap.MeasurementArea({
 *  viewer: viewer
 * });
 *
 * const InteractionMeasurementArea = new Ditap.InteractionMeasurementArea();
 * InteractionMeasurementArea.measurementArea = measurementArea;
 */
class MeasurementArea {
  constructor(options) {
    const viewer = options.viewer;
    const positions = options.positions;
    const entityColor = options.entityColor;

    this._id = `MeasurementArea${createGuid()}`;

    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;

      viewer.ditapStorage.measurementArea[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
    } else {
      this._entityColor = Color.fromBytes(38, 60, 150, 255);
    }

    this._positions = [];
    this._pointEntities = [];
    this._polylineEntities = [];
    this._labelHeight = undefined;
    this._polygonPrimitives = [];
    this._labelEntity = undefined;
    this._centerPosition = undefined;
    this._resizeObserver = undefined;
    this._windowResizeEvent = undefined;
    this._entityNum = 0;
    this._cameraChangedEvent = undefined;
    this._displayCondition = 2000;
    if (positions) {
      if (this.checkPositionsToCreate(positions)) {
        this.positions = positions;
        this.createEntity();
      } else {
        console.error("More than three positions are required.");
      }
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  get viewer() {
    return this._viewer;
  }
  set viewer(viewer) {
    this._viewer = viewer;
  }

  /*
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @type {string}
   */
  get id() {
    return this._id;
  }
  set id(id) {
    this._id = id;
  }

  /**
   * 면적 측정 할 Cartesian 배열</br>
   * Cartesian array for measuring area.
   * @type {Cartesian3[]}
   * @readonly
   */
  get positions() {
    return this._positions;
  }
  set positions(positions) {
    this._positions = positions;
  }

  /**
   * 면적 측정으로 얻은 Point Entity 배열.</br>
   * Array of point entities resulting from area measurements.
   * @type {Entity[]}
   * @readonly
   */
  get pointEntities() {
    return this._pointEntities;
  }
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }

  /**
   * 면적 측정으로 얻은 Polyline Entity 배열.</br>
   * Array of polyline entities resulting from area measurements.
   * @type {Entity[]}
   * @readonly
   */
  get polylineEntities() {
    return this._polylineEntities;
  }
  set polylineEntities(polylineEntities) {
    this._polylineEntities = polylineEntities;
  }

  /**
   * 면적 측정으로 얻은 polygon Primitive 배열.</br>
   * Array of polygon primitives resulting from area measurements.
   * @type {Primitive[]}
   * @readonly
   */
  get polygonPrimitives() {
    return this._polygonPrimitives;
  }
  set polygonPrimitives(polygonPrimitives) {
    this._polygonPrimitives = polygonPrimitives;
  }

  /*
   * 면적 측정으로 얻은 Label Entity 배열 요소.</br>
   * Array of label elements resulting from area measurements.
   * @type {Element}
   */
  get labelEntity() {
    return this._labelEntity;
  }
  set labelEntity(labelEntity) {
    this._labelEntity = labelEntity;
  }

  /*
   * 라벨 내부 높이. </br>
   * Internal height of the label element.
   * @type {number}
   */
  get labelHeight() {
    return this._labelHeight;
  }
  set labelHeight(labelHeight) {
    this._labelHeight = labelHeight;
  }

  /*
   * 면적 엔티티의 중심 Cartesian 값.</br>
   * Center cartesians of the are Entity.
   * @type {Cartesian3}
   */
  get centerPosition() {
    return this._centerPosition;
  }
  set centerPosition(centerPosition) {
    this._centerPosition = centerPosition;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  get entityNum() {
    return this._entityNum;
  }
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 엔티티 색상.<br>
   * Color of the entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePointColor(this.pointEntities, entityColor);
      Utils.changePolylineColor(this.polylineEntities, entityColor);
      Utils.changePolygonPrimitiveColor(this.polygonPrimitives, entityColor);
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /*
   * Ditap Viewer에서 특정 포인트 엔티티 제거.</br>
   * Remove point entity from viewer.
   * @param {Entity} entity 제거될 엔티티.</br> entity to remove.
   */
  removePointEntity(entity) {
    const findEntity = (findentity) => findentity === entity;
    const index = this.pointEntities.findIndex(findEntity);
    this.pointEntities.splice(index, index + 1);
    this.viewer.entities.remove(entity);
  }

  /*
   * Ditap Viewer에서 특정 폴리라인 엔티티 제거.</br>
   * Remove polyline entity from viewer.
   * @param {Entity} entity 제거될 엔티티.</br> entity to remove.
   */
  removePolylineEntity(entity) {
    const findEntity = (findentity) => findentity === entity;
    const index = this.polylineEntities.findIndex(findEntity);
    this.polylineEntities.splice(index, index + 1);
    this.viewer.entities.remove(entity);
  }

  /*
   * Ditap Viewer에서 특정 폴리곤 프리미티브 제거.</br>
   * Remove polygon primitive from viewer.
   */
  removePolygonPrimitives() {
    this.polygonPrimitives.forEach((primitive) => {
      this.viewer.scene.primitives.remove(primitive);
    });
    this.polygonPrimitives = [];
  }

  /*
   * 저장된 특정 좌표 제거.</br>
   * Remove position from created position array.
   * @param {Cartesian3} position 제거될 좌표.</br> Cartesian3 to remove.
   */
  removePositions(position) {
    const findPosition = (findposition) => findposition === position;
    const index = this.positions.findIndex(findPosition);
    this.positions.splice(index, index + 1);
  }

  /*
   * 좌표 배열 간의 중심좌표 계산.</br>
   * Calculate the center point between coordinate arrays.
   * @param {Cartesian3[]} positions 중심 좌표를 계산할 좌표 배열.</br>Cartesian array to calculate the center point.
   * @return {Cartesian3} 중심 좌표. </br>Center position.
   */
  getCenter(positions) {
    const cs = BoundingSphere.fromPoints(positions).center;
    const ctg = Cartographic.fromCartesian(cs);

    const lon = cesiumMath.toDegrees(ctg.longitude);
    const lat = cesiumMath.toDegrees(ctg.latitude);
    const height = this.viewer.scene.globe.getHeight(ctg);

    const center = Cartesian3.fromDegrees(lon, lat, height);
    return center;
  }

  /*
   * 세 좌표의 면적 계산.</br>
   * Calculate the area of the triangle.
   * @param {Cartesian3} p 첫번째 좌표.</br> Position1
   * @param {Cartesian3} q 두번째 좌표.</br> Position2
   * @param {Cartesian3} r 세번째 좌표.</br> Position3
   * @returns {number} 계산된 세 좌표의 면적.</br> Area of the calculated triangle.
   */
  getTriangleArea(p, q, r) {
    let vec1 = Cartesian3.subtract(q, p, new Cartesian3());
    vec1 = Cartesian3.normalize(vec1, vec1);
    let vec2 = Cartesian3.subtract(r, p, new Cartesian3());
    vec2 = Cartesian3.normalize(vec2, vec2);
    const len1 = Cartesian3.distance(p, q);
    const len2 = Cartesian3.distance(p, r);

    const dot = Cartesian3.dot(vec1, vec2, new Cartesian3()); // cos
    const theta = Math.acos(dot);

    return len1 * len2 * Math.sin(theta) * 0.5;
  }

  /*
   * 좌표 배열의 면적 계산.</br>
   * Calculate the area of the coordinate array.
   * @param {Cartesian3[]} positions 계산될 좌표 배열.</br>Cartesian arrangement to calculate the area.
   * @returns {object} 계산된 면적 object.</br> Calculated area object.
   */
  calculateArea(positions) {
    let totalArea = 0;

    const cartographics = [];
    /* 입력된 point가 시계방향인지 반시계방향인지 확인  */
    for (let i = 0, len = positions.length; i < len; i++) {
      const cartographic = Cartographic.fromCartesian(positions[i]);
      cartographics.push(
        new Cartesian2(cartographic.longitude, cartographic.latitude)
      );
    }
    const polygonPoints = [];
    const triangles = PolygonPipeline.triangulate(cartographics);
    for (let i = 0, len = triangles.length; i < len; i += 3) {
      const p = positions[triangles[i]];
      const q = positions[triangles[i + 1]];
      const r = positions[triangles[i + 2]];
      const area = this.getTriangleArea(p, q, r);
      totalArea += area;
      polygonPoints.push([p, q, r]);

      // const polygonPrimitives = this.createPolygon(
      //   [p, q, r],
      //   Color.CORNFLOWERBLUE.withAlpha(0.3)
      // );
      // this.polygonPrimitives = polygonPrimitives;
    }

    return {
      positionsOfPolygons: polygonPoints,
      area: totalArea.toFixed(2),
    };
  }

  /*
   * 좌표 중심 계산.</br>
   * Calculate center cartesians among cartesians.
   * @param {Cartesian3[]} cartesians Cartesian 배열.</br> cartesian array.
   * @returns {Cartesian3} 계산된 좌표 중심 값.</br> Calculated center cartesian.
   */
  getCenterPosition(positions) {
    return BoundingSphere.fromPoints(positions).center;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    if (this.pointEntities) {
      this.pointEntities.forEach((pointEntity) => {
        this.viewer.entities.remove(pointEntity);
      });
    }
    if (this.polylineEntities) {
      this.polylineEntities.forEach((polylineEntity) => {
        this.removePolylineEntity(polylineEntity);
      });
    }
    if (this.polygonPrimitives) {
      this.removePolygonPrimitives();
    }
    if (this.labelEntity) {
      this.labelEntity.remove();
    }
    if (this.label instanceof Element) {
      this.label.removeEventListener("mouseover", Utils.hideLabelElement);
      this.label.removeEventListener("mouseover", Utils.showLabelElement);
    }

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this.id);

    this.removeChangeEvent();

    this.id = undefined;
    this.positions = [];
    this.pointEntities = [];
    this.polylineEntities = [];
    this.labelHeight = undefined;
    this.polygonPrimitives = [];
    this.labelEntity = undefined;
    this.centerPosition = undefined;
    this.resizeObserver = undefined;
    this.entityNum = 0;
    this.cameraChangedEvent = undefined;
    this.windowResizeEvent = undefined;
  }

  /*
   * 생성된 엔티티 색상 변경.</br>
   * Change the color of the generated entity
   * @param {Entity} entity 변경 할 엔티티.</br> Entities to change.
   * @param {Color} color 변경 할 색상.</br> Color to change.
   */
  setEntityColor(entity, color) {
    if (entity.point) {
      entity.point.color = color;
    } else if (entity.polyline) {
      entity.polyline.material = color;
    } else if (entity.polygon) {
      entity.polygon.material = color;
    }
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const viewer = this.viewer;
    const container = this.viewer.container;
    const labelEntity = this.labelEntity;
    const positions = this.centerPosition;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    const resizeOriginal = () => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    };

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);

    this.windowResizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.windowResizeEvent);
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;
    const labelEntity = this.labelEntity;
    const positions = this.centerPosition;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntity,
        positions,
        displayCondition,
        height
      );
    });
  }

  /*
   * 면적 측정 가능 좌표인지 구별.</br>
   * Determine if the coordinates are suitable for measuring area.
   * @param {Cartesian3[]} positions 측정할 좌표 배열.<br> Array of Cartetian3 to measure.
   * @returns {boolean} 구분 결과.</br> result.
   */
  checkPositionsToCreate(positions) {
    let isCartesians = true;
    positions.forEach((position) => {
      if (!(position instanceof Cartesian3)) {
        isCartesians = false;
      }
    });

    if (positions.length >= 3 && isCartesians) {
      return true;
    }
    return false;
  }

  /*
   * Ditap Viewer에서 MeasurementArea 엔티티 생성.</br>
   * Create MeasurementArea Entities in the Ditap Viewer.
   */
  createEntity() {
    const displayCondition = Utils.getDistanceDisplayConditon(this.positions);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    // POLINT - 생성
    this.positions.forEach((position) => {
      const pointEntity = Utils.createPoint({
        viewer: this.viewer,
        id: this.id + this.entityNum,
        position: position,
        outlineColor: this.entityColor,
        displayCondition: this.displayCondition,
      });
      this.entityNum++;
      this.pointEntities.push(pointEntity);
    });

    // POLYLINE - 생성
    this.polylineEntities.push(
      Utils.createPolyline({
        viewer: this.viewer,
        id: this.id + this.entityNum,
        positions: [...this.positions, this.positions[0]],
        material: this.entityColor,
        displayCondition: this.displayCondition,
      })
    );
    this.entityNum++;

    const result = this.calculateArea(this.positions);
    const area = result.area;
    const positionsOfPolygons = result.positionsOfPolygons;

    // POLYGON - 생성
    positionsOfPolygons.forEach((positionsOfPolygon) => {
      const polygonPrimitive = Utils.createPolygonPrimitive({
        viewer: this.viewer,
        id: this.id + this.entityNum,
        positions: positionsOfPolygon,
        material: this.entityColor.withAlpha(0.3),
        displayCondition: this.displayCondition,
      });
      this.entityNum++;
      this.polygonPrimitives.push(polygonPrimitive);
    });

    // LABEL - 생성
    this.centerPosition = this.getCenterPosition(this.positions);

    this.labelEntity = Utils.createTotalLabel(this.viewer, area, true);

    this.labelHeight = this.labelEntity.clientHeight;
    Utils.changeLabelLocation(
      this.viewer,
      this.centerPosition,
      this.labelEntity,
      this.labelHeight
    );

    this.createWindowResizeEvent();
    this.createCameraMoveEvent();
  }
}

export default MeasurementArea;
